import { config } from '@/config';
import { allowTracking, isServer } from '@/helpers';
import { Confidence, Context } from '@spotify-confidence/sdk';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '.';

type ConfidenceClient = ReturnType<typeof Confidence.create>;

type ConfidenceCtxValue = {
  client: ConfidenceClient;
  updateContext: (patch: Context) => void;
  isReady: boolean;
};

const confidenceClientSingleton = Confidence.create({
  clientSecret: config.confidenceClientKey,
  region: 'eu',
  environment: 'client',
  timeout: 1000,
  context: {
    app_environment: config.env === 'dev' ? 'development' : 'production',
    platform: 'web',
  },
});

export const updateConfidenceContext = (patch: Context) => {
  try {
    if (!allowTracking()) return;
    confidenceClientSingleton.setContext(patch);
  } catch (err) {
    console.warn('Confidence setContext failed:', err);
  }
};

const ConfidenceContext = createContext<ConfidenceCtxValue | null>(null);

export const useConfidenceClient = (): ConfidenceCtxValue | undefined => {
  const ctx = useContext(ConfidenceContext);
  if (isServer) return;
  if (!ctx) {
    throw new Error('useConfidenceClient must be used within <ConfidenceExperimentProvider>');
  }
  return ctx;
};

type ProviderProps = { children: React.ReactNode };

export const ConfidenceExperimentProvider = ({ children }: ProviderProps) => {
  const clientRef = useRef<ConfidenceClient>(confidenceClientSingleton);
  const [isContextReady, setIsContextReady] = React.useState(false);

  const user = useAppSelector((state) => state.users?.user);

  useEffect(() => {
    if (allowTracking() && user?.emailHash) {
      clientRef.current.setContext({ user_id: user.emailHash });
    }
  }, [user?.emailHash]);

  const updateContext = useCallback((patch: Context) => {
    updateConfidenceContext(patch);
    // Mark as ready after first context update (device_id is set)'
    setIsContextReady(true);
  }, []);

  const value = useMemo<ConfidenceCtxValue>(() => {
    return {
      client: clientRef.current,
      updateContext,
      isReady: isContextReady,
    };
  }, [updateContext, isContextReady]);

  return <ConfidenceContext.Provider value={value}>{children}</ConfidenceContext.Provider>;
};
